var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { action, computed, observable, when, makeObservable } from 'mobx';
import { runtime } from '../../../runtime/Runtime';
import { round } from '../../../../utils/helper/lodash_replacements';
export class AspectMountMeasureDom {
    parent;
    measureRound = 0;
    constructor(parent) {
        makeObservable(this);
        this.parent = parent;
    }
    get aspectMount() {
        return this.parent;
    }
    get aspect() {
        return this.parent.aspect;
    }
    get domElement() {
        return this.aspectMount.domElement;
    }
    get measuredClient() {
        const triggerOnManualMeasure = this.measureRound;
        return {
            top: this.domElement.clientTop,
            left: this.domElement.clientLeft,
            width: this.domElement.clientWidth,
            height: this.domElement.clientHeight,
        };
    }
    get measuredOffset() {
        const triggerOnManualMeasure = this.measureRound;
        /*
        if (this.domElement.nodeType === Node.TEXT_NODE) {
          const range = document.createRange();
          range.selectNode(this.domElement);
          const rect = range.getBoundingClientRect();
          range.detach();
          return rect;
        }
    
         */
        const pageOffset = calculatePageOffset(this.domElement);
        return {
            top: pageOffset.top,
            left: pageOffset.left,
            width: this.domElement.offsetWidth,
            height: this.domElement.offsetHeight,
        };
    }
    get measuredScroll() {
        const triggerOnManualMeasure = this.measureRound;
        return {
            top: this.domElement.scrollTop,
            left: this.domElement.scrollLeft,
            width: this.domElement.scrollWidth,
            height: this.domElement.scrollHeight,
        };
    }
    get expectedOffset() {
        const { expectedWidth, expectedHeight, expectedTop, expectedLeft } = this.aspect.persistentAspect.scaledValueOf;
        return {
            top: expectedTop || 0,
            left: expectedLeft || 0,
            width: this.aspect.scaledValueOf.width || expectedWidth || this.aspect.scaledValueOf.minWidth || 0,
            height: this.aspect.scaledValueOf.height || expectedHeight || this.aspect.scaledValueOf.minHeight || 0,
        };
    }
    get expectedOrMeasuredOffset() {
        if (!this.domElement) {
            return this.expectedOffset;
        }
        const rect = this.measuredOffset;
        if (runtime.runtimePermissions.hasEditFunctionality) {
            runtime.runtimeScheduler.addToNextIdleStateChange(() => {
                this.saveExpectedBoundsOnEdit();
            });
        }
        return rect;
    }
    savedBounds = false;
    async saveExpectedBoundsOnEdit() {
        await when(() => !!runtime.streamedPackages.Edit);
        if (!this.domElement)
            return;
        if (this.savedBounds)
            return;
        const { scaledValueOf } = this.aspect.persistentAspect;
        this.savedBounds = true;
        const rect = this.measuredOffset;
        const measuredStageBounds = this.aspect.context.stage.inner.aspectMount.measureDom.measuredOffset; // do not use computed because of cycle
        const topToInnerStage = rect.top - measuredStageBounds.top;
        const leftToInnerStage = rect.left - measuredStageBounds.left;
        if (Math.abs(scaledValueOf.expectedWidth - rect.width) > 5)
            scaledValueOf.expectedWidth = round(rect.width, 1);
        if (Math.abs(scaledValueOf.expectedHeight - rect.height) > 5)
            scaledValueOf.expectedHeight = round(rect.height, 1);
        if (Math.abs(scaledValueOf.expectedTop - topToInnerStage) > 5)
            scaledValueOf.expectedTop = round(topToInnerStage, 1);
        if (Math.abs(scaledValueOf.expectedLeft - leftToInnerStage) > 5)
            scaledValueOf.expectedLeft = round(leftToInnerStage, 1);
    }
    get measuredStyle() {
        const triggerOnManualMeasure = this.measureRound;
        if (!this.domElement || this.domElement.nodeType === Node.TEXT_NODE) {
            return {
                marginTop: '0',
                marginRight: '0',
                marginBottom: '0',
                marginLeft: '0',
                fontSize: '0',
            };
        }
        return getComputedStyle(this.domElement);
    }
    get parsedStyle() {
        return {
            marginTop: parseInt(this.measuredStyle.marginTop || '0'),
            marginRight: parseInt(this.measuredStyle.marginRight || '0'),
            marginBottom: parseInt(this.measuredStyle.marginBottom || '0'),
            marginLeft: parseInt(this.measuredStyle.marginLeft || '0'),
            fontSize: parseInt(this.measuredStyle.fontSize || '0'),
        };
    }
    get expectedOrMeasuredBoundingClientRect() {
        if (!this.domElement)
            return this.expectedOffset;
        // includes Transformations etc.
        return this.measuredBoundingClientRect;
    }
    get measuredBoundingClientRect() {
        const triggerOnManualMeasure = this.measureRound;
        // includes Transformations etc.
        const rect = this.domElement.getBoundingClientRect();
        return {
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        };
    }
    measureManually() {
        this.measureRound++;
    }
    setupObservers(domElement) {
        if (!domElement)
            return undefined;
        if (domElement === this.lastObserving)
            return;
        // only need Mutation observer for autoTextsize
        const growGroupName = this.aspect.aspectClassNames.textGrowChildGroupName;
        if (growGroupName) {
            this.autoSizeText(growGroupName);
        }
        this.setupResizeObserver(domElement);
        if (growGroupName) {
            this.setupMutationObserver(domElement);
        }
        this.lastObserving = domElement;
    }
    lastObserving;
    lastResizeObserver;
    setupResizeObserver(domElement) {
        if (this.lastResizeObserver) {
            this.lastResizeObserver.disconnect();
        }
        // https://caniuse.com/#feat=resizeobserver
        let ResizeObserver = window.ResizeObserver;
        if (!ResizeObserver) {
            if (!runtime.availablePackages.Lazy)
                return undefined; // Only Internet Explorer
            ResizeObserver = runtime.availablePackages.Lazy.classes.ResizeObserverPolyfill;
        }
        const resizeObserver = new ResizeObserver(entries => {
            this.autoSizeTextWhenIdle();
            runtime.runtimeScheduler.scheduleForAnimationFrame(() => this.measureRound++);
        });
        // measure on setup
        //  runInAction(() => this.measureRound++);
        runtime.runtimeScheduler.addToNextIdleStateChange(() => this.measureRound++);
        resizeObserver.observe(domElement);
        this.lastResizeObserver = resizeObserver;
    }
    lastMutationObserver;
    setupMutationObserver(domElement) {
        if (!runtime.runtimePermissions.isAutoTextSizingEnabled)
            return;
        if (this.lastMutationObserver) {
            this.lastMutationObserver.disconnect();
        }
        const mutationObserver = new MutationObserver(mutations => {
            this.autoSizeTextWhenIdle();
            /*
                    if (runtime.runtimePermissions.hasEditFunctionality) {
                      console.log('mutation observed in Aspect', this.aspect.readableIdentifier);
      
                      mutations.forEach(function (mutation) {
                        console.log(mutation.type);
                      });
            } */
        });
        const config = {
            attributes: runtime.runtimePermissions.hasEditFunctionality,
            childList: true,
            characterData: true,
            subtree: true,
        };
        // eigentliche Observierung starten und Zielnode und Konfiguration übergeben
        mutationObserver.observe(domElement, config);
        this.lastMutationObserver = mutationObserver;
    }
    autoSizeTextWhenIdle() {
        if (!runtime.runtimePermissions.isAutoTextSizingEnabled)
            return;
        const growGroupName = this.aspect.aspectClassNames.textGrowChildGroupName;
        if (!growGroupName)
            return;
        const question = runtime.solidBrowserRuntime.app.question;
        runtime.runtimeScheduler.addToNextIdleStateChange(() => {
            if (runtime.solidBrowserRuntime.app.question === question) {
                this.autoSizeText(growGroupName);
            }
        }, { skipIfContains: growGroupName });
    }
    autoSizeText(textGrowChildGroupName) {
        if (!runtime.runtimePermissions.isAutoTextSizingEnabled)
            return;
        const { performer } = this.aspect.context.app.textsizer.getTextSizeGroup(textGrowChildGroupName);
        performer.performSizing();
    }
}
__decorate([
    observable
], AspectMountMeasureDom.prototype, "measureRound", void 0);
__decorate([
    computed.struct
], AspectMountMeasureDom.prototype, "measuredClient", null);
__decorate([
    computed.struct
], AspectMountMeasureDom.prototype, "measuredOffset", null);
__decorate([
    computed.struct
], AspectMountMeasureDom.prototype, "measuredScroll", null);
__decorate([
    computed({
        equals: (a, b) => !['top', 'left,', 'width', 'height'].map(attr => Math.abs(a[attr] - b[attr])).find(i => i > 3),
    })
], AspectMountMeasureDom.prototype, "expectedOrMeasuredOffset", null);
__decorate([
    computed.struct
], AspectMountMeasureDom.prototype, "measuredStyle", null);
__decorate([
    computed({
        equals: (a, b) => !['top', 'left,', 'width', 'height'].map(attr => Math.abs(a[attr] - b[attr])).find(i => i > 3),
    })
], AspectMountMeasureDom.prototype, "expectedOrMeasuredBoundingClientRect", null);
__decorate([
    action.bound
], AspectMountMeasureDom.prototype, "measureManually", null);
function calculatePageOffset(el) {
    let _x = 0;
    let _y = 0;
    while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
        _x += el.offsetLeft - el.scrollLeft;
        _y += el.offsetTop - el.scrollTop;
        el = el.offsetParent;
    }
    return { top: _y, left: _x };
}
